/**
 * Backward-compatible Button adapter.
 *
 * Existing consumers import the default export with the legacy API
 * (variant, size, isLoading, fullWidth).  Internally this delegates to
 * the CVA-based primitive in `button-primitives.tsx`.
 *
 * New code should import directly from `../ui/button-primitives` for the
 * full CVA API (asChild, ghost/link variants, icon size, buttonVariants).
 *
 * NOTE: `button-primitives.tsx` is used instead of `button.tsx` because
 * macOS APFS is case-insensitive — `Button.tsx` and `button.tsx` would
 * collide.  On Linux (Vercel builds) they are distinct, but keeping them
 * as separate names avoids local-dev confusion.
 */
import React, { ButtonHTMLAttributes } from 'react';
import { Button as BaseButton } from './button-primitives';
import { cn } from '../../lib/utils';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}) => {
  return (
    <BaseButton
      variant={variant}
      size={size}
      className={cn(fullWidth && 'w-full', className)}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Processing...
        </>
      ) : (
        children
      )}
    </BaseButton>
  );
};

export default Button;
